import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { clientViewsService } from '../services/api';
import PropertyGallery from '@/components/PropertyGallery';
import { Button } from '@/components/ui/button';
import {
  ArrowLeft, Share2, Check, BedDouble, Bath,
  Maximize2, MessageSquare, Building2,
} from 'lucide-react';

interface Property {
  id: string;
  title: string;
  price: number;
  bedrooms: number;
  bathrooms: number;
  area_m2: number;
  images?: string[];
}

interface ClientViewItem {
  id: string;
  property_id: string;
  position: number;
  property: Property | null;
}

interface Note {
  id: string;
  property_id: string | null;
  content: string;
  created_at: string;
}

interface ClientView {
  id: string;
  title: string;
  created_at: string;
  items: ClientViewItem[];
  notes: Note[];
}

function DetailSkeleton() {
  return (
    <div className="min-h-[100dvh] bg-background">
      <div className="max-w-5xl mx-auto px-6 py-8 animate-pulse space-y-6">
        <div className="h-8 bg-stone-100 rounded-lg w-64" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-32 bg-stone-100 rounded-xl" />
          ))}
        </div>
      </div>
    </div>
  );
}

function ClientViewDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [clientView, setClientView] = useState<ClientView | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [noteContent, setNoteContent] = useState('');
  const [noteError, setNoteError] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [shareLink, setShareLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [shareError, setShareError] = useState('');

  const loadClientView = useCallback(async () => {
    try {
      const data = await clientViewsService.get(id!);
      setClientView(data);
    } catch {
      setLoadError('No se pudo cargar la vista. Intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadClientView();
  }, [loadClientView]);

  const handleAddNote = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!noteContent.trim()) return;
    setNoteError('');
    setAddingNote(true);
    try {
      await clientViewsService.addNote(id!, null, noteContent);
      setNoteContent('');
      loadClientView();
    } catch {
      setNoteError('Error al agregar la nota. Intenta de nuevo.');
    } finally {
      setAddingNote(false);
    }
  };

  const handleGenerateShareLink = async () => {
    setShareError('');
    try {
      const response = await clientViewsService.generateShareLink(id!, 168);
      const url = `${window.location.origin}/public/views/${response.token}`;
      setShareLink(url);
      await navigator.clipboard.writeText(url).catch(() => {});
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setShareError('Error al generar el link. Intenta de nuevo.');
    }
  };

  if (loading) return <DetailSkeleton />;

  if (loadError || !clientView) {
    return (
      <div className="min-h-[100dvh] bg-background flex items-center justify-center px-6">
        <div className="text-center">
          <Building2 className="w-10 h-10 mx-auto mb-4 text-muted-foreground/30" />
          <p className="font-medium text-muted-foreground">{loadError || 'Vista no encontrada'}</p>
          <button
            onClick={() => navigate('/properties')}
            className="mt-4 text-sm text-primary hover:underline"
          >
            Volver a propiedades
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] bg-background">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm border-b border-border">
        <div className="max-w-5xl mx-auto px-6 h-14 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => navigate('/properties')}
              aria-label="Volver"
              className="text-muted-foreground hover:text-foreground transition-colors shrink-0"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <span className="font-medium text-sm truncate">{clientView.title}</span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {shareError && <p className="text-xs text-destructive">{shareError}</p>}
            <Button
              size="sm"
              variant="outline"
              onClick={handleGenerateShareLink}
              className="transition-all active:scale-[0.98] gap-1.5"
            >
              {copied ? (
                <><Check className="w-3.5 h-3.5 text-green-600" /> Copiado</>
              ) : (
                <><Share2 className="w-3.5 h-3.5" /> Compartir</>
              )}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        {/* Share link display */}
        {shareLink && (
          <div className="mb-6 p-4 bg-white border border-border rounded-xl">
            <p className="text-xs font-medium text-muted-foreground mb-1.5">Link compartible</p>
            <p className="text-xs font-mono text-foreground/70 break-all">{shareLink}</p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-8 items-start">
          {/* Properties column */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold">
                Propiedades
                <span className="ml-2 text-xs font-normal text-muted-foreground">
                  ({clientView.items.length})
                </span>
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {clientView.items.map((item) => (
                <div
                  key={item.id}
                  className="bg-white rounded-xl border border-border overflow-hidden"
                >
                  <div className="h-1 bg-primary/30" />
                  <div className="p-5">
                    {item.property ? (
                      <>
                        <div className="mb-3">
                          <PropertyGallery images={item.property.images ?? []} />
                        </div>
                        <h3 className="font-medium text-sm leading-snug line-clamp-2 mb-3">
                          {item.property.title}
                        </h3>
                        <p className="text-lg font-semibold text-primary mb-3">
                          ${item.property.price.toLocaleString('es-MX')}
                          <span className="text-xs font-normal text-muted-foreground ml-1.5">MXN</span>
                        </p>
                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <BedDouble className="w-3.5 h-3.5" />
                            {item.property.bedrooms} rec.
                          </span>
                          <span className="flex items-center gap-1">
                            <Bath className="w-3.5 h-3.5" />
                            {item.property.bathrooms} baños
                          </span>
                          <span className="flex items-center gap-1">
                            <Maximize2 className="w-3.5 h-3.5" />
                            {item.property.area_m2} m²
                          </span>
                        </div>
                      </>
                    ) : (
                      <p className="text-sm text-muted-foreground">Detalles no disponibles</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Notes column */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <MessageSquare className="w-4 h-4 text-muted-foreground" />
              <h2 className="font-semibold">
                Notas
                <span className="ml-2 text-xs font-normal text-muted-foreground">
                  ({clientView.notes.length})
                </span>
              </h2>
            </div>

            <form onSubmit={handleAddNote} className="mb-4">
              <textarea
                value={noteContent}
                onChange={(e) => setNoteContent(e.target.value)}
                placeholder="Agrega una nota sobre esta selección..."
                rows={3}
                className="w-full px-3 py-2.5 text-sm bg-white border border-border rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-shadow"
              />
              {noteError && <p className="text-xs text-destructive mt-1">{noteError}</p>}
              <Button
                type="submit"
                size="sm"
                disabled={addingNote || !noteContent.trim()}
                className="mt-2 w-full transition-all active:scale-[0.98]"
              >
                {addingNote ? 'Guardando...' : 'Agregar nota'}
              </Button>
            </form>

            {clientView.notes.length > 0 ? (
              <div className="space-y-2">
                {clientView.notes.map((note) => (
                  <div key={note.id} className="bg-white border border-border rounded-xl p-4">
                    <p className="text-sm leading-relaxed">{note.content}</p>
                    <p className="text-xs text-muted-foreground mt-2">
                      {new Date(note.created_at).toLocaleString('es-MX', {
                        day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
                      })}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground/50">
                <MessageSquare className="w-7 h-7 mx-auto mb-2" />
                <p className="text-xs">Sin notas aún</p>
              </div>
            )}
          </div>
        </div>

        <p className="text-xs text-muted-foreground mt-8">
          Creado el {new Date(clientView.created_at).toLocaleDateString('es-MX', {
            day: 'numeric', month: 'long', year: 'numeric',
          })}
        </p>
      </main>
    </div>
  );
}

export default ClientViewDetailPage;
